/**
 * SignOutContext — provides a sign-out callback to the component tree
 * (task 3.13; Req 20.1, 20.2, 20.3).
 *
 * This module does NOT import from `@viamericas/auth-react` or the adapters
 * layer — it is a pure React context so that components importing it (App.tsx)
 * do not transitively pull in the restricted package at test time. The actual
 * sign-out callback is supplied by the provider in `main.tsx`.
 *
 * Components consume it via `useSignOutContext()`, which returns `null` when
 * no provider is mounted, signaling that the sign-out button should not render.
 */

import { createContext, useContext, type ReactNode } from "react";

const SignOutCtx = createContext<(() => void) | null>(null);

export interface SignOutProviderProps {
  /** The sign-out callback to expose to the tree. */
  value: () => void;
  children: ReactNode;
}

/**
 * Provider that exposes the sign-out callback. The value is built externally
 * (in main.tsx) using the auth adapters.
 */
export function SignOutProvider({ value, children }: SignOutProviderProps) {
  return (
    <SignOutCtx.Provider value={value}>{children}</SignOutCtx.Provider>
  );
}

/**
 * Consume the sign-out callback. Returns `null` when no auth provider is
 * present (test environments), signaling the sign-out button should not render.
 */
export function useSignOutContext(): (() => void) | null {
  return useContext(SignOutCtx);
}
